import { Language } from "@src/reducers/your-reducer";

const LOCALE_MAP: Record<NonNullable<Language>, string> = {
  en: "en",
  de: "de",
  zh: "zh-CN",
  it: "it",
  es: "es",
  pt: "pt-BR",
  nl: "nl",
  tr: "tr-TR",
};

export const toBCP47 = (language: Language): string =>
  language ? LOCALE_MAP[language] ?? "en" : "en";

const getUTCOffset = (timezone: string): string => {
  try {
    const parts = new Intl.DateTimeFormat("en", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "UTC";
  } catch {
    return "UTC";
  }
};

/**
 * Returns the localized timezone name only, e.g. "中国标准时间" or "China Standard Time".
 * The IANA id (e.g. "Asia/Shanghai") is never shown to the user.
 */
export const getLocalizedTimezoneName = (
  timezone: string,
  language: Language,
): string => {
  const locale = toBCP47(language);
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      timeZoneName: "long",
    }).formatToParts(new Date());
    return (
      parts.find((p) => p.type === "timeZoneName")?.value ??
      timezone.replace(/_/g, " ")
    );
  } catch {
    return timezone.replace(/_/g, " ");
  }
};

/**
 * Formats a date value for use in chart tooltips, respecting the user's configured
 * timezone and language. If no timezone is set, falls back to UTC and appends the timezone abbreviation.
 *
 * @param dateValue - The date to format (ISO string, timestamp, or Date object)
 * @param timezone - The IANA timezone string from the store (null / undefined = UTC)
 * @param language - The app language from the store (null / undefined = "en")
 */
export const formatChartTooltipDate = (
  dateValue: string | number | Date,
  timezone: string | null | undefined,
  language: Language,
): string => {
  const locale = toBCP47(language);
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone || "UTC",
    ...(timezone ? {} : { timeZoneName: "short" }),
  }).format(new Date(dateValue as string));
};

/**
 * Formats a date value for use in chart x-axis labels, respecting the user's
 * configured timezone. Returns a compact two-line string: "DD-MM-YYYY\nHH:MM".
 *
 * @param dateValue - The date to format (ISO string, timestamp, or Date object)
 * @param timezone - The IANA timezone string from the store (null / undefined = UTC)
 */
export const formatChartXAxisDate = (
  dateValue: string | number | Date,
  timezone: string | null | undefined,
): string => {
  const date = new Date(dateValue as string);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone || "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const map: Record<string, string> = {};
  parts.forEach((p) => {
    map[p.type] = p.value;
  });

  const day = map.day ?? "01";
  const month = map.month ?? "01";
  const year = map.year ?? "1970";
  const hours = map.hour ?? "00";
  const minutes = map.minute ?? "00";

  return `${day}-${month}-${year}\n${hours}:${minutes}`;
};

/**
 * Returns the full display label used in the selector, e.g. "(GMT+8) 中国标准时间".
 * The IANA id is never shown to the user.
 */
export const getTimezoneDisplayName = (
  timezone: string,
  language: Language,
): string => {
  const offset = getUTCOffset(timezone);
  const name = getLocalizedTimezoneName(timezone, language);
  return `(${offset}) ${name}`;
};
